// NodeFormFieldsList.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Hooks
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { hasNodeFieldDisplayValue } from 'utilities/nodeFieldDisplayValue';

// Components
import NodeField from 'node/form/field/NodeField';
// import NodeFormSlot from 'node/form/slot/NodeFormSlot';

/**
 * NodeFormFieldsList
 *
 * Renders the ordered field rows and insertion slots for a node form.
 *
 * @param {array} fields - Resolved field definitions for the active node type.
 * @param {object} node - Active node record.
 * @param {string} nodeTypeKey - Key for the active node type.
 * @param {object} language - Active language context value.
 * @param {function} getType - Resolves field type definitions.
 * @param {function} onChange - Called with node patch updates.
 * @param {function} onCoverSaved - Callback when cover media is saved.
 * @param {function} onLocationSaved - Callback when location is saved.
 * @param {function} onSubtitleSaved - Callback when description is saved.
 * @param {function} onTagsSaved - Callback when tags are saved.
 * @param {boolean} canAddFields - Whether new fields can be inserted.
 * @param {boolean} canManageFields - Whether existing fields can be edited/reordered/deleted.
 * @param {function} onSettingsClick - Opens field settings for a field.
 * @param {function} onFieldChange - Handles direct field value updates.
 * @param {string} draggingFieldName - Field name currently being dragged.
 * @param {string} dragOverFieldName - Field name currently targeted for drop.
 * @param {function} onDragStart - Starts a drag interaction for a field.
 * @param {function} onDragOver - Handles drag-over behavior for a field.
 * @param {function} onDrop - Handles drop behavior for a field.
 * @param {function} onDragEnd - Ends the current drag interaction.
 * @param {function} onAddField - Opens the add-field flow for a slot index.
 * @param {boolean} previewMode - Read-only / visitor: valued fields only, no hover/slots.
 * @param {boolean} [autoFocusRichText=false] - Autofocus the first richtext field.
 * @param {string|number} [autoFocusKey] - Refocus token for the autofocused richtext field.
 *
 * @returns {ReactNode} The rendered list of node form fields.
 */
const NodeFormFieldsList = ({
    fields,
    node,
    nodeTypeKey,
    language,
    getType,
    onChange,
    onCoverSaved,
    onLocationSaved,
    onSubtitleSaved,
    onTagsSaved,
    canAddFields = false,
    canManageFields = false,
    onSettingsClick,
    onFieldChange,
    draggingFieldName: _draggingFieldName,
    dragOverFieldName: _dragOverFieldName,
    onDragStart: _onDragStart,
    onDragOver: _onDragOver,
    onDrop: _onDrop,
    onDragEnd: _onDragEnd,
    onAddField,
    previewMode = false,
    autoFocusRichText = false,
    autoFocusKey = '',
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // Hooks
    const isMobile = useIsMobile();

    // ----- STATE ----- //

    const [hoveredFieldName, setHoveredFieldName] = useState('');

    const renderFields = useMemo(() => {
        const isDocumentNodeType = String(nodeTypeKey || '').toUpperCase() === 'DOCUMENT';
        const baseFields = (Array.isArray(fields) ? fields : []).filter((field) => {
            if (isDocumentNodeType && field?.name === 'securedUrl') return false;
            return true;
        });

        if (!previewMode) return baseFields;

        return baseFields.filter((field) => hasNodeFieldDisplayValue({
            field,
            node,
            nodeTypeKey,
        }));
    }, [fields, node, nodeTypeKey, previewMode]);

    const firstRichTextFieldName = useMemo(() => {
        const match = renderFields.find((field) => (
            String(field?.type || '').trim().toLowerCase() === 'richtext'
        ));
        return match?.name || '';
    }, [renderFields]);

    // ----- RENDER ----- //

    return renderFields.map((field, index) => {
        // Field reorder on the live form is paused — schema order edits live in
        // collection settings. Restore by re-enabling canDrag + handlers below.
        // const isDragging = _draggingFieldName === field.name;
        // const isDropTarget = _dragOverFieldName === field.name;
        const wrapperClassName = [
            // isDragging ? 'dragging' : '',
            // isDropTarget ? 'drop-target' : '',
            previewMode ? 'is-preview' : '',
        ]
            .filter(Boolean)
            .join(' ');

        // const canDrag = canManageFields && Boolean(node?.key && nodeTypeKey);
        const showHover = !previewMode && hoveredFieldName === field.name;

        // Preview / read-only (Peek visitor, Workspace sidebar): soft faded
        // wash instead of panel chrome. Editable forms keep panel + hover.
        const idleBackground = previewMode ? theme.backgroundFaded : theme.panel;

        const wrapperProps = {
            className: wrapperClassName,
            // draggable: canDrag,
            'data-field-name': field.name,
            style: {
                backgroundColor: showHover ? theme.backgroundHover : isMobile ? theme.backgroundMuted : theme.backgroundHover, //idleBackground,
                //borderColor: theme.outline,
                // backgroundColor: isDropTarget
                //     ? theme.backgroundMuted
                //     : (showHover ? theme.backgroundHover : idleBackground),
                // borderColor: isDropTarget ? theme.foregroundMuted : 'transparent',
                // borderStyle: isDropTarget ? 'dashed' : 'solid',
                // cursor: showHover && canDrag ? 'grab' : undefined,
            },
            onMouseEnter: previewMode ? undefined : () => setHoveredFieldName(field.name),
            onMouseLeave: previewMode ? undefined : () => setHoveredFieldName(''),
        };

        // if (canDrag) {
        //     wrapperProps.onDragEnd = _onDragEnd;
        //     wrapperProps.onDragOver = (event) => _onDragOver(field, event);
        //     wrapperProps.onDragStart = (event) => _onDragStart(field, event);
        //     wrapperProps.onDrop = (event) => _onDrop(field, event);
        // }

        return (
            <React.Fragment key={field.name}>

                {/* NODE FORM ADD SLOT 
                {!previewMode ? (
                    <NodeFormSlot
                      groupName="General"
                      index={index + 1}
                      disabled={!canAddFields}
                      onAdd={onAddField}
                    />
                ) : null}
                */}

                <NodeField
                  field={field}
                  node={node}
                  nodeTypeKey={nodeTypeKey}
                  language={language}
                  getType={getType}
                  showSettings={canManageFields}
                  handleFieldChange={onFieldChange}
                  wrapperProps={wrapperProps}
                  onChange={onChange}
                  onCoverSaved={onCoverSaved}
                  onLocationSaved={onLocationSaved}
                  onSettingsClick={canManageFields ? onSettingsClick : null}
                  onSubtitleSaved={onSubtitleSaved}
                  onTagsSaved={onTagsSaved}
                  previewMode={previewMode}
                  autoFocusRichText={
                    autoFocusRichText
                    && Boolean(firstRichTextFieldName)
                    && field.name === firstRichTextFieldName
                  }
                  autoFocusKey={autoFocusKey}
                />

            </React.Fragment>
        );
    });
};

// Dev Tools
NodeFormFieldsList.displayName = 'NodeFormFieldsList';

export default NodeFormFieldsList;
